import React, { useEffect, useMemo } from "react";
import { Alert, Stack, Typography, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

import FormAlert from "../components/common/FormAlert";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { postStagedTransaction } from "../redux/actions/bankTransactionStagingActions";
import { getTerms } from "../redux/actions/termActions";
import { getStudents } from "../redux/actions/studentActions";
import { bankTransactionStagingActions } from "../redux/slices/bankTransactionStaging";

import { BankTransactionStagingResponseDto } from "../shared/dtos/bankTransaction/BankTransactionStagingResponseDto";
import { PostStagedTransactionRequestDto } from "../shared/dtos/bankTransaction/PostStagedTransactionRequestDto";

const validationSchema = (isExactMatch: boolean) =>
  Yup.object().shape({
    termId: Yup.string().required("Term is required"),
    overrideStudentId: isExactMatch
      ? Yup.string().nullable()
      : Yup.string().required(
          "You must manually select a student for unmatched records.",
        ),
  });

interface PostStagedTransactionFormProps {
  stagingRecord: BankTransactionStagingResponseDto;
  loading: boolean;
  error: string | null;
}

const PostStagedTransactionForm: React.FC<PostStagedTransactionFormProps> = ({
  stagingRecord,
  loading,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: terms } = useAppSelector((state) => state.term);
  const { items: students } = useAppSelector((state) => state.student);

  const isExactMatch = stagingRecord?.matchConfidence === "EXACT";

  useEffect(() => {
    if (!terms || terms.length === 0) {
      dispatch(getTerms({ activeOnly: true }));
    }
    if (!isExactMatch && (!students || students.length === 0)) {
      dispatch(getStudents());
    }
  }, [dispatch, terms, students, isExactMatch]);

  const termOptions = useMemo(
    () => terms?.map((t) => ({ value: t.id, label: t.name })) || [],
    [terms],
  );

  const studentOptions = useMemo(
    () =>
      students?.map((s) => ({
        value: s.id,
        label: `${s.firstName} ${s.lastName} (${s.studentId || "No ID"})`,
      })) || [],
    [students],
  );

  const initialValues: PostStagedTransactionRequestDto = {
    stagingId: stagingRecord.id,
    termId: "",
    overrideStudentId: isExactMatch ? stagingRecord.matchedStudentId : "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            bankTransactionStagingActions.setErrorPostingStagedTransaction(
              null,
            ),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema(isExactMatch)}
        onSubmit={(values) => {
          dispatch(
            postStagedTransaction({
              stagingId: stagingRecord.id,
              termId: values.termId,
              overrideStudentId: isExactMatch ? null : values.overrideStudentId,
            }),
          );
        }}
      >
        {({ isValid }: FormikProps<PostStagedTransactionRequestDto>) => (
          <Form>
            <Stack spacing={3}>
              {/* Informational Header */}
              <Stack spacing={2}>
                <Alert
                  severity={isExactMatch ? "success" : "warning"}
                  sx={{ textAlign: "left" }}
                >
                  {isExactMatch
                    ? `System successfully matched this payment to: ${stagingRecord.matchedStudentName} (${stagingRecord.matchedAdmissionNumber})`
                    : "System could not auto-match this payment. Please select the correct student manually."}
                </Alert>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    bgcolor: "background.default",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 600 }}
                    >
                      Bank Ref
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {stagingRecord?.rawReference || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 600 }}
                    >
                      Amount
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", color: "secondary.main" }}
                    >
                      KES {stagingRecord?.amount?.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ gridColumn: "span 2" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 600 }}
                    >
                      Narrative
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {stagingRecord?.rawDescription || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Stack>

              {/* Form Fields */}
              <FormSelectField
                name="termId"
                label="Apply Payment To Term"
                options={termOptions}
              />

              {!isExactMatch && (
                <FormSelectField
                  name="overrideStudentId"
                  label="Select Student Manually"
                  options={studentOptions}
                />
              )}

              <SubmitButton
                title="Post & Allocate Payment"
                loadingText="Posting..."
                loading={loading}
                disabled={!isValid || loading}
                startIcon={<CheckCircleOutlineIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default PostStagedTransactionForm;
